import { defineStore } from "pinia";
import { GetSelfReceive, GetUnread } from "@/api/message/message";
import { useUserStore } from "/@/store/modules/user";
import { getLocal, saveLocal } from "/@/utils";
import Socket from "@/uni_modules/c-websocket/js_sdk/c-websocket.js";

interface MessageState {
	unread: number;
	unread_message_type_one_count: number;
	unread_message_type_zero_count: number;
	total: number;
	today_count: number;
	msgList: any[];
	page: number;
	limit: number;
	hasMore: boolean;
	loading: boolean;
	receiveMsgSwitch: boolean;
	targetType: any;
}

function getReceiveMsgFromLocal() {
	const val = getLocal("receiveMsg");
	if (typeof val === "boolean") return val;
	if (val === "true") return true;
	if (val === "false") return false;
	return true;
}

// 将 socketInstance 作为模块级变量，不放到 state 里，避免 Proxy/私有字段问题
let socketInstance: any = null;

export const useMessageStore = defineStore("messageStore", {
	state: (): MessageState => ({
		unread: 0,
		unread_message_type_one_count: 0,
		unread_message_type_zero_count: 0,
		total: 0,
		today_count: 0,
		msgList: [],
		page: 1,
		limit: 20,
		hasMore: true,
		loading: false,
		receiveMsgSwitch: getReceiveMsgFromLocal(),
		targetType: "",
	}),

	actions: {
		// 获取消息列表（支持分页、追加/覆盖）
		async getMsgList({
			page = 1,
			limit = 20,
			append = false,
			refresh = false,
			...otherParams
		} = {}) {
			if (this.loading) return;
			if (!refresh) this.loading = true;
			uni.showLoading();
			try {
				const res = await GetSelfReceive({
					page,
					limit,
					...otherParams,
				});
				uni.hideLoading();
				if (res.code === 2000) {
					const list = res.data.data.message_data || [];
					if (append) {
						this.msgList = [...this.msgList, ...list];
					} else {
						this.msgList = list;
					}
					this.total = res.data.data.all_count;
					this.unread = res.data.data.unread_count;
					this.today_count = res.data.data.today_count;
					this.unread_message_type_one_count =
						res.data.data.unread_message_type_one_count;
					this.unread_message_type_zero_count =
						res.data.data.unread_message_type_zero_count;
					this.page = page;
					this.limit = limit;
					this.hasMore = list.length === limit;
				}
			} finally {
				uni.hideLoading();
				this.loading = false;
			}
		},

		async loadMore() {
			if (!this.hasMore || this.loading) return;
			await this.getMsgList({
				page: this.page + 1,
				limit: this.limit,
				append: true,
				refresh: false,
			});
		},

		async refresh(query = {}, refresh = true) {
			this.page = 1;
			this.hasMore = true;
			await this.getMsgList({
				page: 1,
				limit: this.limit,
				append: false,
				refresh,
				...query,
			});
		},

		async getUnreadNum() {
			// const res = await GetUnread();
			const res = await GetSelfReceive({});
			if (res.code === 2000) {
				this.total = res.data.data.all_count;
				this.unread = res.data.data.unread_count;
				this.today_count = res.data.data.today_count;
				this.unread_message_type_one_count =
					res.data.data.unread_message_type_one_count;
				this.unread_message_type_zero_count =
					res.data.data.unread_message_type_zero_count;
			}
		},

		// 新 WebSocket 插件初始化
		initWebSocket() {
			if (socketInstance) {
				this.closeWebSocket();
			}
			const userStore = useUserStore();
			let wsUri =
				import.meta.env.VITE_APP_BASE_URL + "/ws/" + userStore.token + "/";
			// 将hppts://替换为wss://，否则会报错
			wsUri = wsUri.replace("https://", "wss://");
			socketInstance = new Socket("messageSocket");
			socketInstance.setReconnectParams({
				isExecute: true,
				count: 5,
				time: 3000,
			});
			socketInstance.setHeartbeatParams({
				open: true,
				time: 10000,
				data: JSON.stringify({ type: "ping" }),
			});
			socketInstance.connect({
				url: wsUri,
				onOpen: (res: any) => {
					console.log("WebSocket已连接", res);
				},
				onMessage: (res: any) => {
					try {
						let data = JSON.parse(res.data);
						console.log("接收服务端的消息", data);
						if (data.content !== "连接成功") {
							if (data.contentType === "SYSTEM") {
								// this.refresh();
								// this.getUnreadNum();
								// if (this.receiveMsgSwitch) {}
								$baseMessageTop(data.content, "primary");
							} else if (data.contentType === "ERROR") {
								if (this.receiveMsgSwitch) {
									$baseMessageTop(data.content, "error");
								}
							}
						}
					} catch (e) {
						console.error("WebSocket 消息解析失败", e);
					}
				},
				onClose: (res: any) => {
					console.log("WebSocket已关闭", res.code, res.reason, res);
				},
				onError: (err: any) => {
					console.error("WebSocket错误", err);
				},
			});
		},

		// 关闭 WebSocket
		closeWebSocket() {
			if (
				socketInstance &&
				typeof (socketInstance as any).close === "function" &&
				(socketInstance as any).constructor &&
				(socketInstance as any).constructor.name === "SocketManager"
			) {
				try {
					(socketInstance as any).close((res: any) => {
						console.log("WebSocket关闭情况：", res);
					});
				} catch (e) {
					console.error("WebSocket关闭异常", e);
				}
				socketInstance = null;
			} else {
				socketInstance = null;
			}
		},

		// 发送消息
		sendWebSocketMsg(msg) {
			if (socketInstance) {
				(socketInstance as any).send(JSON.stringify(msg));
			} else {
				console.warn("WebSocket未连接");
			}
		},

		setReceiveMsgSwitch(val: boolean) {
			this.receiveMsgSwitch = !!val;
			saveLocal("receiveMsg", !!val);
		},

		setMessageList(val: any[]) {
			this.msgList = val;
		},

		setTargetType(val: string) {
			this.targetType = val;
			this.refresh();
		},

		setUnreadNum(val: number) {
			this.unread = val;
		},
	},
});
